import { useCallback, useContext } from 'react';
import Kulturio from '@ekultur/kulturio-bridge';
import { AppContext } from '../contexts/AppContext';

export type TriggerProtocol = "udp" | "tcp" | "websocket";

export interface Trigger {
  protocol?: string,
  ip?: string,
  port?: number | string,
  command?: string
}

interface ValidTrigger {
  protocol: TriggerProtocol,
  ip: string,
  port: number,
  command: string
}

/** Close websocket connections that never open */
const WEBSOCKET_TIMEOUT = 5000;

/**
 * Strict IPv4 check (four octets, 0-255, no leading zeros)
 */
const isValidIp = (ip: unknown): ip is string => {
  if (typeof ip !== "string") { return false; }
  const parts = ip.trim().split(".");
  return parts.length === 4 && parts.every(part => /^(0|[1-9]\d{0,2})$/.test(part) && Number(part) <= 255);
};

/**
 * Parse a port from a number or numeric string, 1-65535
 */
const parsePort = (port: unknown): number | null => {
  const value = typeof port === "string" && /^\d+$/.test(port.trim()) ? Number(port) : port;
  return typeof value === "number" && Number.isInteger(value) && value >= 1 && value <= 65535 ? value : null;
};

/**
 * Validate a trigger from the CMS. Returns null if protocol, ip, port or command is missing or malformed.
 */
export const validateTrigger = (trigger: Trigger | null | undefined): ValidTrigger | null => {
  const protocol = String(trigger?.protocol || "").trim().toLowerCase();
  const port = parsePort(trigger?.port);
  const command = trigger?.command;

  if (protocol !== "udp" && protocol !== "tcp" && protocol !== "websocket") { return null; }
  if (!isValidIp(trigger?.ip) || port === null) { return null; }
  if (typeof command !== "string" || command.trim().length === 0) { return null; }

  return { protocol, ip: trigger!.ip!.trim(), port, command };
};

/**
 * Open a websocket, send one message and close it again
 */
const sendWebsocketMessage = (ip: string, port: number, command: string) => {
  let socket: WebSocket;
  try {
    socket = new WebSocket(`ws://${ip}:${port}`);
  } catch (e) {
    console.warn(`Trigger: could not connect to ws://${ip}:${port}`, e);
    return;
  }

  const timeout = window.setTimeout(() => socket.close(), WEBSOCKET_TIMEOUT);
  socket.addEventListener("open", () => {
    socket.send(command);
    window.clearTimeout(timeout);
    socket.close();
  });
  socket.addEventListener("error", () => {
    window.clearTimeout(timeout);
    console.warn(`Trigger: websocket error for ws://${ip}:${port}`);
  });
};

/**
 * Send all valid triggers. Invalid triggers are skipped.
 * @param triggers Triggers from the CMS record
 */
export const sendTriggers = (triggers: Trigger[] | undefined) => {
  if (!Array.isArray(triggers)) { return; }

  triggers.forEach(trigger => {
    const valid = validateTrigger(trigger);
    if (!valid) {
      console.warn("Trigger skipped: missing or malformed protocol, ip, port or command", trigger);
      return;
    }

    const { protocol, ip, port, command } = valid;
    switch (protocol) {
      case "udp":
        if (!Kulturio.sendUDPMessage(ip, port, command)) { console.warn("Trigger: UDP is only available in the Kulturio clients"); }
        break;
      case "tcp":
        if (!Kulturio.sendTCPMessage(ip, port, command)) { console.warn("Trigger: TCP is only available in the Kulturio clients"); }
        break;
      case "websocket":
        sendWebsocketMessage(ip, port, command);
        break;
    }
  });
};

/**
 * Returns a function that runs a record's triggers when `useTriggers` is true. Never runs in preview.
 */
const useRunTriggers = () => {
  const { previewIsActive } = useContext(AppContext);

  return useCallback((useTriggers: boolean | undefined, triggers: Trigger[] | undefined) => {
    if (previewIsActive || useTriggers !== true) { return; }
    sendTriggers(triggers);
  }, [previewIsActive]);
};

export default useRunTriggers;
